import { z } from 'zod/v4';

import { AdditionalServicesGetResponseRuntype } from '../additionalServices';
import {
    AssemblyIndustryRuntype,
    AssemblyTypeRuntype,
    BareIpnIdentifierRuntype,
    BomItemApprovalStatus,
    BomItemIssue,
} from '../assembly/assemblyBackendTypes';
import { CurrencyRuntype, MonetaryValueBackendRuntype, QuantityRuntype } from '../backendTypes';
import { DemandScenarioRuntype, RfqCreationDemandScenarioRuntype } from '../demandScenario/demandScenarioBackendTypes';
import { UserDTORuntype } from '../user';

export enum RfqStatus {
    /**
     * The initial state.
     */
    RequestInDraft = 'RequestInDraft',
    /**
     * When a quotation is requested by the OEM.
     */
    QuotationInProgress = 'QuotationInProgress',
    /**
     * When the EMS uploads a quotation.
     */
    QuotationAvailable = 'QuotationAvailable',
    /**
     * When the customer has placed an order.
     */
    OrderPlaced = 'OrderPlaced',
    /**
     * When the EMS "won" the RfQ.
     */
    OrderConfirmed = 'OrderConfirmed',
    /**
     * When the payment of an order has failed.
     */
    PaymentFailed = 'PaymentFailed',
    /**
     * When the EMS is in the process of procuring the parts from the suppliers.
     */
    OrderInProcurement = 'OrderInProcurement',
    /**
     * When the EMS has the assembly in production.
     */
    OrderInProduction = 'OrderInProduction',
    /**
     * Terminal state.
     * When the EMS has shipped the assembly.
     */
    OrderShipped = 'OrderShipped',
    /**
     * Terminal state. No bid.
     */
    NoQuotation = 'NoQuotation',
    /**
     * Terminal state.
     * When the EMS "lost" the RfQ.
     */
    NoOrder = 'NoOrder',
}
export const RfqStatusRuntype = z.enum(RfqStatus);

export const ProjectProgressRuntype = z.enum(['NotStarted', 'InProgress', 'Done']);
export type ProjectProgress = z.infer<typeof ProjectProgressRuntype>;

export const ProjectAreaRuntype = z.enum(['Design', 'Sourcing', 'Manufacturing', 'Calculation', 'Quotation']);
export type ProjectArea = z.infer<typeof ProjectAreaRuntype>;

export const ProjectProgressEntryRuntype = z.object({
    rfq_id: z.string(),
    area: ProjectAreaRuntype,
    progress: ProjectProgressRuntype,
    updated_at: z.string(),
    updated_by: z.string().nullable().optional(),
});
export type ProjectProgressEntry = z.infer<typeof ProjectProgressEntryRuntype>;

/**
 * Returns true if the given status indicates that work needs to be done on the RfQ.
 *
 * This is used to determine if the RfQ should be included in the "work in progress" chart.
 */
export function isWorkInProgress(status: RfqStatus): boolean {
    return (
        status === RfqStatus.RequestInDraft ||
        status === RfqStatus.QuotationInProgress ||
        status === RfqStatus.QuotationAvailable ||
        status === RfqStatus.OrderPlaced ||
        status === RfqStatus.OrderInProduction ||
        status === RfqStatus.OrderInProcurement
    );
}

export const WorkflowTypeRuntype = z.union([z.literal('Automatic'), z.literal('Manual'), z.literal('DontShow')]);
export type WorkflowType = z.infer<typeof WorkflowTypeRuntype>;

export const PaymentMethodRuntype = z.union([z.literal('InstantOnly'), z.literal('InvoiceOnly'), z.literal('Both')]);
export type PaymentMethodType = z.infer<typeof PaymentMethodRuntype>;

export interface VolumeEstimate extends z.infer<typeof VolumeEstimateRuntype> {}
const VolumeEstimateRuntype = z.object({
    lower: z.string().nullable(),
    upper: z.string().nullable(),
    currency: CurrencyRuntype,
});

export interface EMSRfqDetails extends z.infer<typeof EMSRfqDetailsRuntype> {}
const EMSRfqDetailsRuntype = z.object({
    volume_estimate: z.null().or(VolumeEstimateRuntype),
});

export const RfqUpdateTriggerRuntype = z.enum(['QuotationUploaded']);
export type RfqUpdateTrigger = z.infer<typeof RfqUpdateTriggerRuntype>;

export const EmsRfqUpdateRuntype = z.object({
    name: z.string().optional(),
    internal_number: z.string().nullable().optional(),
    due_date: z.string().nullable().optional(),
    currency: CurrencyRuntype.optional(),
    volume_estimate: VolumeEstimateRuntype.optional(),
    status: RfqStatusRuntype.optional(),
    shipping_tracking_link: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
    internal_notes: z.string().nullable().optional(),
    trigger: RfqUpdateTriggerRuntype.optional(),
});

const ExistingOrderSizeRuntype = z.object({
    sourcing_scenario_id: z.string(),
    quantity: z.number(),
    desired_date: z.string().nullable(),
});

const NewOrderSizeRuntype = z.object({
    quantity: z.number(),
    desired_date: z.string().nullable(),
});

export const CustomerOrderSizeRuntype = z
    .object({
        type: z.literal('Existing'),
        data: ExistingOrderSizeRuntype,
    })
    .or(
        z.object({
            type: z.literal('New'),
            data: NewOrderSizeRuntype,
        }),
    );
export type CustomerOrderSizeDTO = z.infer<typeof CustomerOrderSizeRuntype>;

export const CustomerRfqUpdateRuntype = z.object({
    name: z.string().optional(),
    industry: AssemblyIndustryRuntype.optional(),
    order_sizes: z.array(CustomerOrderSizeRuntype).optional(),
    additional_services: z.array(z.string()),
});

export const RfqUpdateRuntype = z
    .object({
        type: z.literal('Ems'),
        data: EmsRfqUpdateRuntype,
    })
    .or(
        z.object({
            type: z.literal('Customer'),
            data: CustomerRfqUpdateRuntype,
        }),
    );

export interface CustomerRfqUpdateDTO extends z.infer<typeof CustomerRfqUpdateRuntype> {}
export interface EmsRfqUpdateDTO extends z.infer<typeof EmsRfqUpdateRuntype> {}

export const SalesOrderOverviewRuntype = z.object({
    id: z.string(),
    estimated_lead_time_in_days: z.number().nullable(),
    order_size: z.number(),
    total_price: MonetaryValueBackendRuntype,
    created_at: z.string(),
});

export interface RfqListItemDTO extends z.infer<typeof RfqListItemRuntype> {}
export const RfqListItemRuntype = z.object({
    id: z.string(),
    name: z.string(),
    customer_name: z.string(),
    customer_number: z.string().nullable(),
    default_contact_person: UserDTORuntype.nullable(),
    ems_internal_number: z.string().nullable(),
    status: RfqStatusRuntype,
    creation_date: z.string(),
    due_date: z.string().nullable(),
    ems_rfq_details: EMSRfqDetailsRuntype,
    created_by: UserDTORuntype.nullable(),
    contributors: z.array(
        z.object({
            name: z.string(),
            email: z.string(),
        }),
    ),
    notes: z.string().nullable(),
    internal_notes: z.string().nullable(),
    assembly_types: z.array(AssemblyTypeRuntype),
    is_archived: z.boolean(),
    currency: CurrencyRuntype,
    additional_services: z.array(AdditionalServicesGetResponseRuntype.pick({ id: true, name_en: true, name_de: true })),
    sales_order: SalesOrderOverviewRuntype.nullable(),
    industries: z.array(AssemblyIndustryRuntype),
    assembly_count: z.number(),
});

export interface RfqAssemblyDTO extends z.infer<typeof RfqAssemblyRuntype> {}
export const RfqAssemblyRuntype = z.object({
    id: z.string(),
    designator: z.string(),
    assembly_type: AssemblyTypeRuntype,
});

export interface RfqDTO extends z.infer<typeof RfqDTORuntype> {}

export const RfqDTORuntype = z.object({
    creation_date: z.string(),
    due_date: z.string().nullable(),
    ems_rfq_details: EMSRfqDetailsRuntype,
    id: z.string(),
    name: z.string(),
    customer: z.string(),
    top_level_assemblies: z.array(RfqAssemblyRuntype),
    sourcing_scenarios: z.array(z.string()),
    demand_scenarios: z.array(DemandScenarioRuntype),
    status: RfqStatusRuntype,
    shipping_tracking_link: z.string().nullable().optional(),
    ems_internal_number: z.string().nullable(),
    design_items_count: z.number(),
    assemblies_count: z.number(),
    assemblies_types: z.array(AssemblyTypeRuntype),
    created_by: z.string().nullable(),
    currency: CurrencyRuntype,
    is_archived: z.boolean(),
    workflow_type: WorkflowTypeRuntype,
    industry: AssemblyIndustryRuntype,
    notes: z.string().nullable(),
    internal_notes: z.string().nullable(),
});

export const CustomerIdInfoRuntype = z.object({
    type: z.literal('ExistingCustomer').or(z.literal('NewCustomer')),
    data: z.string(),
});

export type CustomerInfoDTO = z.infer<typeof CustomerInfoRuntype>;
export const CustomerInfoRuntype = z.object({
    customer: CustomerIdInfoRuntype,
});

export const AssembliesRuntype = z.array(
    z
        .object({
            name: z.string(),
            assemblyType: AssemblyTypeRuntype,
        })
        .or(z.object({ id: z.string(), assemblyType: AssemblyTypeRuntype })), // if we're duplicating an assembly
);

export type AssemblyInfoDTO = z.infer<typeof AssemblyInfoRuntype>;
export const AssemblyInfoRuntype = z.object({
    industry: AssemblyIndustryRuntype,
    assemblies: AssembliesRuntype,
});

export type RfqInfoDTO = z.infer<typeof RfqInfoRuntype>;
export const RfqInfoRuntype = z.object({
    name: z.string(),
    emsInternalNumber: z.string().or(z.undefined()).nullable(),
    contributors: z.array(z.string()),
    dueDate: z.string().or(z.undefined()).nullable(),
    currency: CurrencyRuntype,
    volumeEstimate: VolumeEstimateRuntype.or(z.undefined()).nullable(),
    comment: z.string().nullable(),
});

export type RfqInputDTO = z.infer<typeof RfqInputDTORuntype>;
export const RfqInputDTORuntype = z.object({
    customer: CustomerInfoRuntype,
    assembly: AssemblyInfoRuntype,
    rfq: RfqInfoRuntype,
    additional_service_ids: z.array(z.string()),
    demand_scenarios: z.array(RfqCreationDemandScenarioRuntype),
});

export type UsedInAssemblyData = z.infer<typeof UsedInAssemblyDataRuntype>;
export const UsedInAssemblyDataRuntype = z.object({
    id: z.string(),
    assembly_ipn: BareIpnIdentifierRuntype.nullable(),
    designator: z.string(),
    rfq: z
        .object({
            id: z.string(),
            name: z.string(),
            status: RfqStatusRuntype,
            creation_date: z.string(),
        })
        .nullable()
        .optional(),
    customer: z.null().or(z.object({ id: z.string(), name: z.string(), number: z.string().optional() })),
    initial_matched_bom_item: z.object({ ids: z.array(z.string()) }),
    creation_date: z.string(),
});

export const UsedInResponseRuntype = z.record(z.string(), z.array(UsedInAssemblyDataRuntype));

const BomIssueInfoRuntype = z.object({
    approval_status: z.enum(BomItemApprovalStatus),
    design_item_ids: z.array(z.string()),
    count: z.number(),
});

export type BomIssueInfo = z.infer<typeof BomIssueInfoRuntype>;

type BomIssuesShape = { [K in BomItemIssue]?: z.ZodOptional<typeof BomIssueInfoRuntype> };

const initialShape: BomIssuesShape = {};

const BomIssuesRuntype = z.object(
    Object.values(BomItemIssue).reduce((acc, issue) => {
        acc[issue] = BomIssueInfoRuntype.optional();
        return acc;
    }, initialShape),
);
export type BomIssues = z.infer<typeof BomIssuesRuntype>;

const PendingStatusRuntype = z.object({ type: z.literal('Pending') });
const DoneStatusRuntype = z.object({ type: z.literal('Done') });
const BomProgressRuntype = z.object({
    type: z.literal('InProgress'),
    issues: BomIssuesRuntype,
});

const BomStatusRuntype = PendingStatusRuntype.or(DoneStatusRuntype).or(BomProgressRuntype);
const RequirementRuntype = z.literal('Required').or(z.literal('Optional')).or(z.literal('DontShow'));
export type CustomerPortalRequirement = z.infer<typeof RequirementRuntype>;
const BomStateRuntype = z.object({
    status: BomStatusRuntype,
    requirement: RequirementRuntype,
});
export type BomState = z.infer<typeof BomStateRuntype>;

const PcbAnalysisInProgressIssueRuntype = z.object({
    type: z.literal('IsAnalyzing'),
    progress: z.number(),
});

const OtherPcbIssuesRuntype = z.object({
    type: z.literal('NoOffer').or(z.literal('NotApproved')).or(z.literal('AnalysisError')).or(z.literal('NoFiles')),
});

const PcbIssueRuntype = PcbAnalysisInProgressIssueRuntype.or(OtherPcbIssuesRuntype);
export type PcbIssue = z.infer<typeof PcbIssueRuntype>;

const PcbIssuesRuntype = z.array(PcbIssueRuntype);
const PcbProgressRuntype = z.object({
    type: z.literal('InProgress'),
    issues: PcbIssuesRuntype,
});
const PcbStatusRuntype = PendingStatusRuntype.or(DoneStatusRuntype).or(PcbProgressRuntype);
const PcbStateRuntype = z.object({
    status: PcbStatusRuntype,
    requirement: RequirementRuntype,
});
export type PcbState = z.infer<typeof PcbStateRuntype>;

const SourcingProgressRuntype = z.object({ type: z.literal('InProgress') });
const SourcingStatusRuntype = PendingStatusRuntype.or(DoneStatusRuntype).or(SourcingProgressRuntype);
const SourcingStateRuntype = z.object({
    status: SourcingStatusRuntype,
    requirement: z.literal('Required'),
});

const FileUploadProgressRuntype = z.object({ type: z.literal('InProgress') });
const FileUploadStatusRuntype = PendingStatusRuntype.or(DoneStatusRuntype).or(FileUploadProgressRuntype);
const FileUploadRuntype = z.object({
    status: FileUploadStatusRuntype,
    requirement: RequirementRuntype,
});
export type CadState = z.infer<typeof FileUploadRuntype>;
export type ManufacturingState = z.infer<typeof FileUploadRuntype>;
export type PnpState = z.infer<typeof FileUploadRuntype>;

export const CustomerPortalAssemblyStateRuntype = z.object({
    bom: BomStateRuntype,
    pcb: PcbStateRuntype,
    cad: FileUploadRuntype,
    manufacturing_files: FileUploadRuntype,
    manufacturing: FileUploadRuntype,
    pnp: FileUploadRuntype,
});

export type CustomerPortalAssemblyState = z.infer<typeof CustomerPortalAssemblyStateRuntype>;

const AssemblyIdRuntype = z.string();

export const CustomerPortalStateRuntype = z.object({
    sourcing_state: SourcingStateRuntype,
    breadcrumbs: z.record(AssemblyIdRuntype, z.array(z.string())),
    assembly_states: z.record(AssemblyIdRuntype, CustomerPortalAssemblyStateRuntype),
});

export type CustomerPortalState = z.infer<typeof CustomerPortalStateRuntype>;

export const AssemblyStateRuntype = z.object({
    is_bom_done: z.boolean(),
    is_pcb_done: z.boolean(),
    is_cad_done: z.boolean(),
    is_manufacturing_files_done: z.boolean(),
    is_manufacturing_done: z.boolean(),
    is_pnp_done: z.boolean(),
});

const QuotationWarningsOk = z.object({
    type: z.literal('Ok'),
});

const QuotationWarningsCalculationMissing = z.object({
    type: z.literal('CalculationMissing'),
    data: z.object({
        assembly_designators: z.array(z.string()),
    }),
});

const QuotationWarningsQuotationDocumentMissing = z.object({
    type: z.literal('QuotationDocumentMissing'),
});

export const QuotationWarningsRuntype = QuotationWarningsOk.or(QuotationWarningsCalculationMissing).or(
    QuotationWarningsQuotationDocumentMissing,
);
export type QuotationWarningsDTO = z.infer<typeof QuotationWarningsRuntype>;

export const RfqStatusHistoryRuntype = z.object({
    rfq_id: z.string(),
    previous_status: RfqStatusRuntype.nullable().optional(),
    new_status: RfqStatusRuntype,
    updated_at: z.string(),
    updated_by: z.string().nullable().optional(),
});

export const ProjectProgressChangeRuntype = z.object({
    rfq_id: z.string(),
    area: ProjectAreaRuntype,
    previous_progress: ProjectProgressRuntype,
    new_progress: ProjectProgressRuntype,
    updated_at: z.string(),
    updated_by: z.string().nullable().optional(),
});
export type ProjectProgressChange = z.infer<typeof ProjectProgressChangeRuntype>;

// ----- /rfqs/:rfqId/quotation-builder-data -----
//
// Mirrors `QuotationBuilderDataResponse` in
// `bounded-context-quotation/builder-data-service-public-types/src/lib.rs`.

const QuotationAddressRuntype = z.object({
    line1: z.string().nullable(),
    line2: z.string().nullable(),
    postal_code: z.string().nullable(),
    city: z.string().nullable(),
    country_code: z.string().nullable(),
});

const QuotationContactPersonRuntype = z.object({
    user_id: z.string(),
    first_name: z.string(),
    last_name: z.string(),
    email: z.string(),
});

const QuotationCustomerInfoRuntype = z.object({
    id: z.string(),
    name: z.string(),
    customer_number: z.string().nullable(),
    billing_address: QuotationAddressRuntype.nullable(),
    default_contact_person: QuotationContactPersonRuntype.nullable(),
});

const QuotationCompanyInfoRuntype = z.object({
    name: z.string(),
    billing_address: z.string().nullable(),
});

const QuotationRfqInfoRuntype = z.object({
    id: z.string(),
    name: z.string(),
    ems_internal_number: z.string().nullable(),
    due_date: z.string().nullable(),
    currency: CurrencyRuntype,
});

const QuotationCostedBomComponentRuntype = z.object({
    designators: z.array(z.string()),
    per_assembly_quantity: QuantityRuntype,
    total_quantity: QuantityRuntype,
    mpn: z.string().nullable(),
    bom_mpn: z.array(z.string()),
    manufacturer: z.string().nullable(),
    supplier: z.string().nullable(),
    description: z.string().nullable(),
    per_assembly_unit_price: MonetaryValueBackendRuntype.nullable(),
    per_assembly_total_price: MonetaryValueBackendRuntype.nullable(),
    total_excess_cost: MonetaryValueBackendRuntype.nullable(),
    total_excess_quantity: QuantityRuntype.nullable(),
    offer_notes: z.string().nullable(),
    sourcing_notes: z.string().nullable(),
    lead_time_days: z.number().nullable(),
    is_consigned: z.boolean(),
});

const QuotationAssemblyScenarioRuntype = z.object({
    sourcing_scenario_id: z.string(),
    name: z.string(),
    // Plain count — `AssemblyQuantity` is `NonZeroU32` in the domain, so a unit-bearing
    // `Quantity` (which BOM items use) would be over-engineering here.
    assembly_quantity: z.number().int(),
    components: z.array(QuotationCostedBomComponentRuntype),
});

const QuotationAssemblyRuntype = z.object({
    id: z.string(),
    parent_id: z.string().nullable(),
    designator: z.string(),
    ipn: BareIpnIdentifierRuntype.nullable(),
    scenarios: z.array(QuotationAssemblyScenarioRuntype),
});

export const QuotationBuilderDataResponseRuntype = z.object({
    rfq: QuotationRfqInfoRuntype,
    customer: QuotationCustomerInfoRuntype,
    company: QuotationCompanyInfoRuntype,
    assemblies: z.array(QuotationAssemblyRuntype),
});
export type QuotationBuilderDataResponse = z.infer<typeof QuotationBuilderDataResponseRuntype>;
export type QuotationAssembly = z.infer<typeof QuotationAssemblyRuntype>;
export type QuotationAssemblyScenario = z.infer<typeof QuotationAssemblyScenarioRuntype>;
export type QuotationCostedBomComponent = z.infer<typeof QuotationCostedBomComponentRuntype>;

// Per-RfQ snapshot of the quotation builder. `state` is an opaque payload owned and
// shaped by the frontend builder (template body + edited data + inputs + section
// toggles); the backend stores it verbatim as JSON. Consumers narrow it themselves.
export const QuotationBuilderStateResponseRuntype = z.object({
    rfq_id: z.string(),
    state: z.unknown(),
    created_at: z.string(),
    updated_at: z.string(),
});
export type QuotationBuilderStateResponse = z.infer<typeof QuotationBuilderStateResponseRuntype>;

export const SaveQuotationBuilderStateRequestRuntype = z.object({
    state: z.unknown(),
});
export type SaveQuotationBuilderStateRequest = z.infer<typeof SaveQuotationBuilderStateRequestRuntype>;
